import { STREAM_API } from '../utils/streamApi';

// ── Analytics report rendering, shared by both analyzer screens ────────────────
// Extracted verbatim from IndustrialVideoAnalyzerPage so the SCHEDULED report
// (Shop Analytics Report, fed from the site's own recordings) renders every
// figure with the same code as the hand-uploaded one. The backend stores the
// analyzer's `results` object unchanged for exactly this reason: two renderers
// would mean two definitions of "customer", and they would drift.
//
// `job` is only read for its id, to build the CSV link. A scheduled window passes
// { id: <its job_id> }; once a window's proofs are past their retention the id is
// gone, and the caller passes csv={false} so no dead link is offered.

const secs = (s) => {
  const n = Math.round(Number(s) || 0);
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  return `${m}m ${String(n % 60).padStart(2, '0')}s`;
};

export function Report({ res, job, proofUrl, onProof, token }) {
  const s = res.summary;
  const show = (name, caption) => onProof({ src: proofUrl(name), caption });
  return (
    <>
      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <span className="ind-eye">Result</span>
          <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
            {secs(s.analysed_secs)} of video at {s.analysis_fps} fps · {s.samples} frames
            examined · took {secs(s.wall_secs)} ({s.realtime_factor}× the clip length)
          </span>
          <span style={{ flex: 1 }} />
          {/* No id once a scheduled window's proofs have aged out of retention —
              the numbers survive, the evidence does not, so offer no dead link. */}
          {job?.id && (
            <a className="ind-pill" href={`${STREAM_API}/ui/analyze/${job.id}/csv?token=${token}`}>
              ⤓ CSV
            </a>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Hero label={s.customer_evidence ? 'CUSTOMERS' : 'VISITORS (UNPROVEN)'}
            val={s.unique_customers} />
          {/* A line at the counter counts approaches, not entries. Labelling both
              "CAME IN" would be the difference between a true number and a wrong one. */}
          {s.line_purpose && (
            <>
              <Stat label={s.line_purpose === 'counter' ? 'CAME TO COUNTER' : 'CAME IN'}
                val={s.customers_in} color="var(--ind-ok)" />
              <Stat label={s.line_purpose === 'counter' ? 'LEFT COUNTER' : 'WENT OUT'}
                val={s.customers_out} color="var(--ind-accent)" />
            </>
          )}
          <Stat label="MOST AT ONCE" val={s.peak_concurrent} />
          <Stat label="AVG TIME SPENT" val={secs(s.avg_dwell_secs)} />
          <Stat label="LONGEST STAY"   val={secs(s.max_dwell_secs)} />
          {s.served_customers != null && (
            <>
              <Stat label="SERVED AT COUNTER" val={s.served_customers} color="#fb7185" />
              <Stat label="AVG AT COUNTER" val={secs(s.avg_service_secs)} color="#fb7185" />
            </>
          )}
          {s.others_seen > 0 && <Stat label="OTHERS PRESENT" val={s.others_seen} color="var(--ind-tx3)" />}
          {s.shelf_opens > 0 && <><Stat label="SHELF OPENINGS" val={s.shelf_opens} color="#facc15" />
              <Stat label="TOTAL OPEN TIME" val={secs(s.shelf_open_secs)} color="#facc15" /></>}
        </div>

        {res.caveats?.length > 0 && (
          <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
            <div className="ind-eye" style={{ marginBottom: 6 }}>What these numbers do and don’t cover</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--ind-tx2)', lineHeight: 1.75 }}>
              {res.caveats.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* customers */}
      <div className="ind-card">
        <span className="ind-eye">Every customer, with proof</span>
        <Table head={['#', 'Proof', 'First seen', 'Last seen', 'Time spent',
          ...(s.served_customers != null ? ['At counter', 'Visits'] : []),
          s.line_purpose === 'counter' ? 'To counter' : 'In',
          s.line_purpose === 'counter' ? 'Away' : 'Out',
          'Crossing proof']}
          rows={(res.customers || []).map(c => [
            c.label,
            c.proof ? <Thumb src={proofUrl(c.proof)} onClick={() => show(c.proof, `${c.label} — best view`)} /> : '—',
            `${c.first_seen}s`, `${c.last_seen}s`, secs(c.dwell_secs),
            ...(s.served_customers != null
              ? [c.service_secs ? secs(c.service_secs) : '—', c.service_visits || '—'] : []),
            c.in, c.out,
            (c.crossings || []).length
              ? (c.crossings.map((x, i) => x.proof && (
                <Thumb key={i} src={proofUrl(x.proof)} small
                  onClick={() => show(x.proof, `${c.label} — ${x.direction.toUpperCase()} at ${x.t}s`)} />
              )))
              : '—',
          ])}
          empty="No customer stayed long enough to count." />
        {s.carried_excluded > 0 && (
          <p style={{ fontSize: 12, color: 'var(--ind-tx3)', marginTop: 8 }}>
            Plus {s.carried_excluded} carried {s.carried_excluded === 1 ? 'child' : 'children'},
            excluded from the customer count.
            {(res.carried || []).map(c => c.proof && (
              <Thumb key={c.track} src={proofUrl(c.proof)} small
                onClick={() => show(c.proof, `${c.label} — carried, not counted`)} />
            ))}
          </p>
        )}
        {s.short_tracks_dropped > 0 && (
          <p style={{ fontSize: 12, color: 'var(--ind-tx3)', marginTop: 4 }}>
            {s.short_tracks_dropped} brief {s.short_tracks_dropped === 1 ? 'sighting' : 'sightings'} under
            the minimum visit length were left out.
            {s.tracks_merged > 0 && ` ${s.tracks_merged} pair(s) of sightings were merged into one
              person by body match.`}
          </p>
        )}
      </div>

      {/* others — present, but neither served nor behind the counter */}
      {(res.others || []).length > 0 && (
        <div className="ind-card">
          <span className="ind-eye">Others — present, but not served and not behind the counter</span>
          <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '6px 0 10px', lineHeight: 1.6 }}>
            On the floor, but never crossed the line and never stood at the counter,
            so there is no evidence they were customers. Someone who works there
            without going behind the counter lands here.
            <br /><b>Read this as unaccounted sightings, not as a headcount</b> — when
            the tracker loses somebody and picks them up again, each piece appears as
            its own row, so this list runs longer than the number of real people.
          </p>
          <Table head={['Who', 'Proof', 'First seen', 'Last seen', 'On floor']}
            rows={res.others.map(o => [
              o.label,
              o.proof ? <Thumb src={proofUrl(o.proof)} onClick={() => show(o.proof, o.label)} /> : '—',
              `${o.first_seen}s`, `${o.last_seen}s`, secs(o.dwell_secs),
            ])}
            empty="" />
        </div>
      )}

      {/* stations — the honest staff headline: how long each post was manned */}
      {(res.stations || []).length > 0 && (
        <div className="ind-card">
          <span className="ind-eye">Stations — how long each post was manned</span>
          <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '6px 0 10px', lineHeight: 1.6 }}>
            Measured exactly, and it claims nothing about <i>who</i>. A post left and
            returned to reads as one station with several sightings — naming the
            person needs a face big enough to match, which a camera looking down at
            the back of someone’s head does not give.
          </p>
          <Table head={['Station', 'Manned', 'Separate sightings']}
            rows={res.stations.map(st => [st.name, secs(st.manned_secs), st.sightings])}
            empty="" />
        </div>
      )}

      {/* staff sightings — the evidence behind the stations above */}
      {(res.staff || []).length > 0 && (
        <div className="ind-card">
          <span className="ind-eye">Staff sightings, with proof</span>
          <Table head={['Sighting', 'Proof', 'First seen', 'Last seen', 'Behind counter', 'In view']}
            rows={res.staff.map(st => [
              st.name ? `${st.name} (matched by face)` : st.label,
              st.proof ? <Thumb src={proofUrl(st.proof)} onClick={() => show(st.proof, `${st.label}`)} /> : '—',
              `${st.first_seen}s`, `${st.last_seen}s`, secs(st.counter_secs), secs(st.seen_secs),
            ])}
            empty="Nobody started inside a staff area." />
        </div>
      )}

      {/* shelf */}
      {(res.shelf || []).length > 0 && (
        <div className="ind-card">
          <span className="ind-eye">Shelf openings</span>
          {/* A zero needs explaining, or it reads as "the feature is broken". These
              say whether anyone was ever in reach and how close the contents came
              to the change threshold. */}
          <Table head={['Shelf', 'Times opened', 'Somebody in reach', 'Hand seen inside',
            'Peak change', 'Threshold', 'Someone standing in it']}
            rows={res.shelf.map(sh => [
              sh.name, sh.opens, secs(sh.watched_secs), sh.wrist_samples,
              sh.max_change, sh.change_threshold,
              sh.occupied_secs ? secs(sh.occupied_secs) : '—',
            ])}
            empty="" />
          {res.shelf.some(sh => sh.occupied_secs > 0.25 * (s.analysed_secs || 1)) && (
            <p style={{ fontSize: 12, color: 'var(--ind-warn, #f59e0b)', marginTop: 8 }}>
              A shelf zone has people standing inside it for much of the clip, so it
              is drawn over floor rather than a shelf face. Openings were ignored while
              it was occupied — redraw it tight to the shelf.
            </p>
          )}
        </div>
      )}
      {(res.shelf_events || []).length > 0 && (
        <div className="ind-card">
          <span className="ind-eye">Every opening, with proof</span>
          <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '6px 0 10px', lineHeight: 1.6 }}>
            A reach is inferred from a hand entering the shelf or the shelf’s contents
            changing while somebody stands at it — there is no sensor on the door, so
            this counts interactions, not openings.
          </p>
          <Table head={['Shelf', 'Opened', 'Closed', 'Open for', 'Evidence', 'Proof']}
            rows={res.shelf_events.map(e => [
              e.shelf, `${e.opened_at}s`,
              e.still_open ? 'still open at the end' : `${e.closed_at}s`,
              e.open_secs != null ? secs(e.open_secs) : '—',
              e.evidence === 'wrist' ? 'hand inside' : 'contents changed',
              e.proof ? <Thumb src={proofUrl(e.proof)}
                onClick={() => show(e.proof, `${e.shelf} opened at ${e.opened_at}s`)} /> : '—',
            ])}
            empty="No openings detected." />
        </div>
      )}
    </>
  );
}

// ── small pieces ──────────────────────────────────────────────────────────────

export function Thumb({ src, onClick, small }) {
  const h = small ? 34 : 54;
  return <img src={src} alt="" onClick={onClick} loading="lazy"
    style={{ height: h, borderRadius: 4, border: '1px solid var(--ind-line)',
      cursor: 'zoom-in', marginRight: 4, verticalAlign: 'middle' }} />;
}

export function Table({ head, rows, empty }) {
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ textAlign: 'left', color: 'var(--ind-tx3)' }}>
          {head.map(h => <th key={h} style={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td style={td} colSpan={head.length}>{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--ind-line)' }}>
              {r.map((c, j) => <td key={j} style={td}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--ind-tx3)' }}>
        {label.toUpperCase()}
      </span>
      {children}
      {hint && <span style={{ fontSize: 10, color: 'var(--ind-tx3)', maxWidth: 220, lineHeight: 1.5 }}>{hint}</span>}
    </div>
  );
}

export function Hero({ label, val }) {
  return (
    <div style={{ flex: '1 1 150px', padding: '14px 12px', borderRadius: 10,
      border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
      <div style={{ fontSize: 44, fontWeight: 800, fontFamily: 'var(--ind-mono)',
        lineHeight: 1, color: 'var(--ind-tx)' }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: '.12em', color: 'var(--ind-tx3)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

export function Stat({ label, val, color }) {
  return (
    <div style={{ flex: '1 1 100px', padding: '14px 10px', borderRadius: 10, textAlign: 'center',
      border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--ind-mono)',
        lineHeight: 1, color: color || 'var(--ind-tx)' }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--ind-tx3)', marginTop: 6 }}>{label}</div>
    </div>
  );
}


export const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)',
  background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
export const th  = { padding: '6px 10px', fontWeight: 600, whiteSpace: 'nowrap' };
export const td  = { padding: '6px 10px', fontFamily: 'var(--ind-mono)' };

export { secs };
